import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, FileText, Shield, Users, AlertTriangle,
  CheckCircle, XCircle, Database, Key, Laptop, Activity
} from 'lucide-react';
import { Button } from '../components/ui/button';

const caseEvidence = [
  { item: 'Policies applied', description: 'Standard Onboarding Policy v2.1, Budget Check Policy', icon: Shield },
  { item: 'Runbooks used', description: 'okta-user-creation, google-workspace-provisioning, intune-enrollment', icon: FileText },
  { item: 'Similar past cases', description: '47 similar onboardings in last 30 days, 98% success rate', icon: Database },
  { item: 'Active incidents', description: 'None affecting Identity or Endpoint systems', icon: AlertTriangle },
  { item: 'Recent changes', description: 'Okta connector updated 3 days ago, no issues reported', icon: Activity },
  { item: 'Identity snapshot', description: 'User: jsmith@acme.com, Dept: Engineering, Manager: mjones@acme.com', icon: Users },
  { item: 'Device snapshot', description: 'MacBook Pro 14", enrolled in Intune, compliant', icon: Laptop }
];

const bundleEvidence = [
  { item: 'Source systems', description: 'BambooHR (hire trigger), Okta (identity), Intune (device)' },
  { item: 'Timestamps', description: 'Created: 2026-01-18T09:00:00Z, Approved: 2026-01-18T09:01:23Z' },
  { item: 'Diffs and affected resources', description: '1 user, 3 groups, 4 apps, 1 device enrollment' },
  { item: 'Verification gates', description: 'Pre-flight: user exists in HRIS. Post-exec: user active in Okta' },
  { item: 'Rollback plan', description: 'Deactivate user, remove from groups, revoke app access' },
  { item: 'Idempotency key', description: 'idem_jsmith_onboard_20260118_001' }
];

const principalChain = [
  { role: 'User', value: 'sarah@acme.com', note: '(requestor via Slack)' },
  { role: 'Approver', value: 'mjones@acme.com', note: '(manager, auto-approved)' },
  { role: 'Agent', value: 'identity_agent_v2.1', note: '' },
  { role: 'Connector', value: 'okta_connector', note: '' },
  { role: 'Service Account', value: 'vedaya-svc@acme.okta.com', note: '' }
];

export const Evidence = () => {
  return (
    <div className="min-h-screen bg-white" data-testid="evidence-page">
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-border">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center justify-between h-14">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-6 h-6 rounded bg-primary flex items-center justify-center">
                <span className="text-primary-foreground font-bold text-xs">V</span>
              </div>
              <span className="text-sm font-semibold text-slate-900">Vedaya</span>
            </Link>
            <nav className="hidden md:flex items-center gap-6">
              <Link to="/workflows" className="text-sm text-muted-foreground hover:text-slate-900">Workflows</Link>
              <Link to="/autonomy" className="text-sm text-muted-foreground hover:text-slate-900">Autonomy</Link>
              <Link to="/integrations" className="text-sm text-muted-foreground hover:text-slate-900">Integrations</Link>
              <Link to="/evidence" className="text-sm font-medium text-primary">Evidence</Link>
              <Link to="/security-governance" className="text-sm text-muted-foreground hover:text-slate-900">Security</Link>
              <Link to="/pricing" className="text-sm text-muted-foreground hover:text-slate-900">Pricing</Link>
            </nav>
            <Button asChild size="sm">
              <Link to="/control-tower">Open Control Tower</Link>
            </Button>
          </div>
        </div>
      </header>

      <section className="py-10 px-6 border-b border-border">
        <div className="max-w-5xl mx-auto">
          <h1 className="text-2xl font-semibold text-slate-900 mb-2">Evidence Model</h1>
          <p className="text-slate-600">
            Every case and action bundle carries evidence documenting the decision rationale,
            policy evaluations, principal chain, and verification results.
          </p>
        </div>
      </section>

      <section className="py-10 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Case Evidence</h2>

          <div className="grid md:grid-cols-2 gap-3">
            {caseEvidence.map((e, idx) => (
              <div key={idx} className="bg-white rounded-lg border border-slate-200 p-4 flex items-start gap-3">
                <div className="w-8 h-8 rounded bg-slate-100 flex items-center justify-center shrink-0">
                  <e.icon className="w-4 h-4 text-slate-600" />
                </div>
                <div>
                  <h3 className="text-sm font-medium text-slate-900">{e.item}</h3>
                  <p className="text-sm text-slate-500">{e.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-10 px-6 bg-slate-50 border-y border-border">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Action Bundle Evidence</h2>

          <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
            <div className="p-4 border-b border-slate-100 bg-slate-50">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded bg-slate-900 flex items-center justify-center">
                  <FileText className="w-4 h-4 text-white" />
                </div>
                <span className="text-sm font-semibold text-slate-900">Bundle: jsmith-onboarding-2026-01-18</span>
                <span className="badge-status badge-active">Executed</span>
              </div>
            </div>
            <div className="divide-y divide-slate-100">
              {bundleEvidence.map((e, idx) => (
                <div key={idx} className="px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4">
                  <span className="text-sm font-medium text-slate-700 w-48 shrink-0">{e.item}</span>
                  <span className="text-sm text-slate-500 font-mono">{e.description}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-10 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Policy Gate Example</h2>

          <div className="bg-slate-100 rounded-lg border border-slate-300 overflow-hidden">
            <div className="p-4 border-b border-slate-300 bg-slate-200">
              <div className="flex items-center gap-2">
                <XCircle className="w-4 h-4 text-slate-700" />
                <span className="text-sm font-semibold text-slate-900">Bundle Blocked: mlee-admin-access</span>
              </div>
            </div>
            <div className="p-4 space-y-3">
              <div className="bg-white rounded-md border border-slate-300 p-3">
                <div className="flex items-center gap-2 mb-2">
                  <XCircle className="w-3.5 h-3.5 text-slate-600" />
                  <span className="text-sm font-medium text-slate-700">FAIL: HR Record Required</span>
                </div>
                <p className="text-sm text-slate-600">
                  No matching employee record found in BambooHR for mlee@acme.com
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  Action: Bundle paused. Notification sent to HR admin.
                </p>
              </div>

              <div className="bg-white rounded-md border border-slate-300 p-3">
                <div className="flex items-center gap-2 mb-2">
                  <XCircle className="w-3.5 h-3.5 text-slate-600" />
                  <span className="text-sm font-medium text-slate-700">FAIL: MFA Enrolled</span>
                </div>
                <p className="text-sm text-slate-600">
                  User mlee@acme.com has MFA status: NOT_ENROLLED in Okta
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  Action: Admin access grant blocked until MFA is enrolled.
                </p>
              </div>

              <div className="bg-white rounded-md border border-slate-200 p-3">
                <p className="text-sm text-slate-700">
                  <span className="font-medium">Resolution:</span> HR must create employee record. User must complete MFA enrollment. Bundle auto-resumes when gates pass.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-10 px-6 bg-slate-50 border-y border-border">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Trace and Audit</h2>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-white rounded-lg border border-slate-200 p-4">
              <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <Key className="w-4 h-4 text-slate-500" />
                Principal Chain
              </h3>
              <p className="text-sm text-slate-600 mb-3">
                Every action carries a chain showing requestor, approver, agent, connector, and credentials.
              </p>
              <div className="bg-slate-50 rounded-md p-3 font-mono text-xs space-y-1">
                {principalChain.map((p, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <span className="text-slate-400 w-24">{p.role}:</span>
                    <span className="text-slate-700">{p.value}</span>
                    <span className="text-slate-400">{p.note}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-lg border border-slate-200 p-4">
              <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <Shield className="w-4 h-4 text-slate-500" />
                Data Handling
              </h3>
              <div className="space-y-2 text-sm text-slate-600">
                <p><span className="font-medium text-slate-700">Retention:</span> Full audit logs retained for 7 years (10 years optional).</p>
                <p><span className="font-medium text-slate-700">Redaction:</span> Secrets, tokens, and PII redacted from logs. Hashes preserved for audit.</p>
                <p><span className="font-medium text-slate-700">Export:</span> JSON format. SIEM-compatible. Splunk, Datadog, Elastic integrations.</p>
                <p><span className="font-medium text-slate-700">Access:</span> Audit log access requires admin role. All access logged.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-10 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Audit Log Sample</h2>

          <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
            <div className="bg-slate-900 p-4 overflow-x-auto">
              <div className="font-mono text-xs text-slate-300 space-y-1 min-w-[600px]">
                <div className="flex gap-3">
                  <span className="text-slate-500 w-36 shrink-0">2026-01-18 09:00:00</span>
                  <span className="text-primary w-28 shrink-0">CASE_CREATED</span>
                  <span>jsmith onboarding initiated by sarah@acme.com via Slack</span>
                </div>
                <div className="flex gap-3">
                  <span className="text-slate-500 w-36 shrink-0">2026-01-18 09:00:12</span>
                  <span className="text-primary w-28 shrink-0">EVIDENCE_GATHERED</span>
                  <span>HR record, manager, budget verified</span>
                </div>
                <div className="flex gap-3">
                  <span className="text-slate-500 w-36 shrink-0">2026-01-18 09:00:45</span>
                  <span className="text-primary w-28 shrink-0">BUNDLE_CREATED</span>
                  <span>6 actions, all low-risk</span>
                </div>
                <div className="flex gap-3">
                  <span className="text-slate-500 w-36 shrink-0">2026-01-18 09:01:23</span>
                  <span className="text-primary w-28 shrink-0">BUNDLE_APPROVED</span>
                  <span>Auto-approved (all gates passed, low-risk)</span>
                </div>
                <div className="flex gap-3">
                  <span className="text-slate-500 w-36 shrink-0">2026-01-18 09:01:30</span>
                  <span className="text-slate-400 w-28 shrink-0">EXECUTING</span>
                  <span>Okta user creation started</span>
                </div>
                <div className="flex gap-3">
                  <span className="text-slate-500 w-36 shrink-0">2026-01-18 09:03:42</span>
                  <span className="text-primary w-28 shrink-0">CASE_CLOSED</span>
                  <span>All actions verified. Ticket ITHELP-2847 created.</span>
                </div>
              </div>
            </div>
            <div className="p-3 bg-slate-50 border-t border-slate-200">
              <p className="text-xs text-slate-500">Full audit trail. Every event timestamped and logged. Exportable in JSON format.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-10 px-6 bg-slate-50 border-t border-border">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-900 mb-1">View evidence in the Control Tower</h2>
            <p className="text-sm text-slate-600">Browse cases, action bundles, and audit logs with full evidence context.</p>
          </div>
          <div className="flex items-center gap-3">
            <Button asChild variant="outline">
              <Link to="/policy-studio">Policy Studio</Link>
            </Button>
            <Button asChild>
              <Link to="/control-tower">
                Open Control Tower
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <footer className="py-6 px-6 border-t border-border">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 rounded bg-primary flex items-center justify-center">
              <span className="text-primary-foreground font-bold text-2xs">V</span>
            </div>
            <span className="text-xs text-slate-500">Vedaya AI</span>
          </div>
          <p className="text-xs text-slate-400">© 2026 Vedaya AI Inc.</p>
        </div>
      </footer>
    </div>
  );
};

export default Evidence;
